/**
 * Shared logic for TransferOwnership Legacy and V2 modals (pure helpers, no stores).
 */

import { validate_email } from 'src/helpers/validators';

/** Default for `settings_transfer_ownership_alert` when missing from i18n. */
export const TRANSFER_OWNERSHIP_ALERT_DEFAULT =
	'Enter the credentials of the recipient account. Selected devices will be transferred and removed from your account.';

/** Default for `transfer_history_not_moved_notice` when missing from i18n. */
export const TRANSFER_HISTORY_NOT_MOVED_NOTICE_DEFAULT =
	"Transferring a device to a new account or home won't move its <strong>event log</strong> or <strong>statistics</strong> data - that history stays with the current account/home.";

/** Default for `transfer_ownership_failure_hint_body` when missing from i18n. */
export const TRANSFER_OWNERSHIP_FAILURE_HINT_BODY_DEFAULT =
	'<p>The transfer did not complete. Common causes include:</p><ul><li>transferring devices to your own account</li><li>no devices eligible for transfer</li><li>incorrect recipient email or password</li><li>one or more devices are currently shared</li><li>wall displays cannot be transferred at this time</li></ul>';

export type TransferActionMode = 'profile' | 'email' | '';

export type TransferInitialState = {
	activeStepIndex: number;
	selectedDevices: Record<string, boolean>;
	actionMode: TransferActionMode;
	selectedEmail: string;
	transferEmail: string;
	profileTransferPassword: string;
	manualTransferPassword: string;
	transferEmailError: string;
	transferPasswordError: string;
	showTransferFailureHelp: boolean;
	transferServerErrorMessage: string;
};

/** Default field values when opening the transfer modal or resetting after profile switch. */
export function createTransferState(): TransferInitialState {
	return {
		activeStepIndex: 0,
		selectedDevices: {},
		actionMode: '' as TransferActionMode,
		selectedEmail: '',
		transferEmail: '',
		profileTransferPassword: '',
		manualTransferPassword: '',
		transferEmailError: '',
		transferPasswordError: '',
		showTransferFailureHelp: false,
		transferServerErrorMessage: '',
	};
}

export type TransferFormatFn = (key: string, opts?: { default?: string }) => string;

export function validateTransferForm(params: {
	actionMode: TransferActionMode;
	selectedEmail: string;
	transferEmail: string;
	password: string;
	formatFn: TransferFormatFn;
}): { emailError: string; passwordError: string; valid: boolean } {
	const { actionMode, selectedEmail, transferEmail, password, formatFn } = params;
	const email = actionMode === 'profile' ? selectedEmail : transferEmail;

	let valid = true;
	let emailError = '';
	let passwordError = '';

	if (!email || !validate_email(email)) {
		emailError = formatFn('input_error_valid_email', {
			default: 'Please enter a valid email!',
		});
		valid = false;
	}

	if (!password) {
		passwordError = formatFn('error_required_field', {
			default: 'This field is required',
		});
		valid = false;
	}

	return { emailError, passwordError, valid };
}

export function getActiveTransferPassword(
	actionMode: TransferActionMode,
	profileTransferPassword: string,
	manualTransferPassword: string,
): string {
	return actionMode === 'profile' ? profileTransferPassword : manualTransferPassword;
}

export function isTransferSubmitDisabled(params: {
	actionMode: TransferActionMode;
	selectedEmail: string;
	transferEmail: string;
	profileTransferPassword: string;
	manualTransferPassword: string;
}): boolean {
	const { actionMode, selectedEmail, transferEmail, profileTransferPassword, manualTransferPassword } =
		params;
	return (
		actionMode === '' ||
		(actionMode === 'profile' && (!selectedEmail || !profileTransferPassword)) ||
		(actionMode === 'email' && (!transferEmail.trim() || !manualTransferPassword))
	);
}

export type TransferCredentialsFields = Pick<
	TransferInitialState,
	| 'actionMode'
	| 'selectedEmail'
	| 'transferEmail'
	| 'profileTransferPassword'
	| 'manualTransferPassword'
	| 'transferEmailError'
	| 'transferPasswordError'
	| 'showTransferFailureHelp'
	| 'transferServerErrorMessage'
>;

/** Credential fields for batch transfer modal when it opens (email mode if no other profiles). */
export function createBatchTransferCredentialsState(
	profilesList: readonly { email: string }[] | null | undefined,
	currentUserEmail: string | undefined,
): TransferCredentialsFields {
	const init = createTransferState();
	const base: TransferCredentialsFields = {
		actionMode: init.actionMode,
		selectedEmail: init.selectedEmail,
		transferEmail: init.transferEmail,
		profileTransferPassword: init.profileTransferPassword,
		manualTransferPassword: init.manualTransferPassword,
		transferEmailError: init.transferEmailError,
		transferPasswordError: init.transferPasswordError,
		showTransferFailureHelp: init.showTransferFailureHelp,
		transferServerErrorMessage: init.transferServerErrorMessage,
	};
	const others = (profilesList ?? []).filter((p) => p.email !== currentUserEmail);
	if (others.length === 0) {
		return { ...base, actionMode: 'email', selectedEmail: '' };
	}
	return base;
}
